import React, { useRef } from "react";
import { Lightning, Trophy, Check, WarningCircle, Trash, ArrowLeft, Scales } from "@phosphor-icons/react";
import { T } from "../themes";
import { BODY_FIELDS, fmtDate, TODAY_ISO, clamp } from "../constants";
import Btn from "../components/Btn";
import Spinner from "../components/Spinner";
import SyncBadge from "../components/SyncBadge";
import BottomNav from "../components/BottomNav";
import MeasurementSparkline from "../components/MeasurementSparkline";
import AppHeader from "../components/AppHeader";

export default function ProgressScreen({
  shell,
  phone,
  OfflineBanner,
  screen,
  setScreen,
  session,
  syncing,
  syncErr,
  persona,
  darkMode, setDarkMode,
  targets,
  dayHistory,
  setDayHistory,
  logHistory,
  weightLogs,
  weightInput,
  setWeightInput,
  weightLogLoading,
  logWeight,
  bodyMeasurements,
  showBodyMeas,
  setShowBodyMeas,
  bodyMeasForm,
  setBodyMeasForm,
  bodyMeasLoading,
  saveBodyMeasurement,
  progressMacros,
  setProgressMacros,
  showProgressFilter,
  setShowProgressFilter,
  saveProfile,
  streakData,
  mealBreakdown,
  hc,
  ACHIEVEMENTS,
  unlockedAchievements,
  achievementToast,
  intelByDay,
  intelLoading,
  loadIntelMeta,
  habitMetrics,
  setHabitMetrics,
  hydrationTarget,
  waterMl,
}) {
  // V61: Build a full 7-calendar-day window. Days not present in dayHistory
  // are represented as null so the table shows all 7 rows and the rolling
  // average is computed only over days that were actually logged.
  // Use logHistory (from logs table) for accurate 7-day data
  const last7Dates = Array.from({length:7}, (_,i) => {
    const d = new Date(); d.setDate(d.getDate() - (6-i));
    return d.toISOString().slice(0, 10);
  }); // oldest first
  const logHistByDate = {};
  (logHistory||[]).forEach(d => { logHistByDate[d.date] = d; });
  const r7 = last7Dates.map(date => logHistByDate[date]?.logged ? { ...logHistByDate[date] } : null);
  const r7Logged = r7.filter(Boolean);
  const avg7 = r7Logged.length > 0
    ? Math.round(r7Logged.reduce((s,d) => s + d.calories, 0) / r7Logged.length)
    : null;
  const failing = avg7 !== null && targets.calories > 0 && avg7 > targets.calories;
  const weightInputRef = useRef(null);
  const scrollToWeightInput = () => {
    setTimeout(() => {
      weightInputRef.current?.scrollIntoView({behavior:"smooth",block:"center"});
      setTimeout(() => weightInputRef.current?.focus(), 350);
    }, 50);
  };
  return (
    <div style={shell}><div style={phone}>
      {OfflineBanner}

      {/* ── Header ─────────────────────────────────────────────────── */}
      <AppHeader
        title="Progress"
        subtitle="Your fitness journey"
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="nutri"
        onAvatarPress={()=>setScreen&&setScreen("settings")}
        username={session?.user?.email}
      />

      <div style={{padding:"14px 16px",overflowY:"auto",flex:1,paddingBottom:24,background:T.pageBg}}>

        {/* ── Log weight shortcut ─────────────────────────────────────── */}
        <button onClick={scrollToWeightInput} style={{
          display:"flex",alignItems:"center",gap:6,
          background:T.accent,border:"none",borderRadius:T.radiusSm,
          padding:"8px 16px",marginBottom:13,cursor:"pointer",
          fontSize:12,fontWeight:600,fontFamily:T.fontSans,color:"#fff",
        }}>
          <Scales size={14} weight="bold" color="#fff"/>Log weight
        </button>

        {failing&&(
          <div style={{backgroundColor:`${T.danger}14`,border:`1px solid ${T.danger}40`,borderLeft:`3px solid ${T.danger}`,padding:"12px 16px",marginBottom:13,display:"flex",gap:9,alignItems:"center",borderRadius:T.radiusSm}}>
            <WarningCircle size={17} weight="fill" color={T.danger} style={{flexShrink:0}}/>
            <div>
              <p style={{color:T.danger,fontSize:13,fontFamily:T.fontDisplay,fontWeight:700,margin:0}}>Needs attention</p>
              <p style={{color:T.inkMuted,fontSize:12,fontFamily:T.fontDisplay,fontWeight:500,margin:"2px 0 0"}}>7-day avg ({avg7} kcal) exceeds target ({targets.calories} kcal) by {avg7-targets.calories} kcal.</p>
            </div>
          </div>
        )}
        {!failing&&avg7!==null&&(
          <div style={{background:T.accent,border:`1px solid ${T.cardBorder}`,padding:"9px 15px",marginBottom:13,display:"flex",alignItems:"center",gap:9,borderRadius:18}}>
            <Check size={15} weight="bold" color={T.ink}/>
            <p style={{color:T.ink,fontSize:11,fontFamily:T.fontDisplay,fontWeight:700,margin:0}}>On track</p>
          </div>
        )}

        {/* ── 7-Day Rolling Average ──────────────────────────────────── */}
        {avg7!==null&&(
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,padding:16,marginBottom:13,display:"flex",alignItems:"center",justifyContent:"space-between",borderRadius:18}}>
            <div>
              <p style={{color:T.inkMuted,fontSize:10,fontWeight:700,fontFamily:T.fontDisplay,letterSpacing:"1px",margin:0}}>7-day rolling average</p>
              <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:500,margin:"2px 0 0"}}>{r7Logged.length} day{r7Logged.length!==1?"s":""} logged of 7</p>
            </div>
            <div style={{textAlign:"right"}}>
              <span style={{color:failing?T.red:T.accent,fontSize:28,fontWeight:800,fontFamily:T.fontDisplay}}>{avg7}</span>
              <span style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontDisplay,fontWeight:500}}> kcal/day</span>
            </div>
          </div>
        )}

        {/* ── Nutrition streak — V25 FIX 11: milestone badges ──────────── */}
        {(()=>{
          const MILESTONES = [7, 14, 30, 60, 90];
          const uid = session?.user?.id || "anon";
          // Persist unlocked badges in localStorage
          const earned = MILESTONES.filter(m => {
            const key = `streak_badge_${uid}_${m}`;
            if (streakData.bestStreak >= m) {
              try { localStorage.setItem(key, "1"); } catch(_){}
              return true;
            }
            try { return localStorage.getItem(key) === "1"; } catch(_){ return false; }
          });
          // Streak recovery: logged yesterday but not today yet
          const yesterday = new Date(); yesterday.setDate(yesterday.getDate()-1);
          const yIso = yesterday.toISOString().slice(0,10);
          const todayIso = new Date().toISOString().slice(0,10);
          const innerLogHistByDate = {};
          (logHistory||[]).forEach(d => { innerLogHistByDate[d.date] = d; });
          const loggedYesterday = innerLogHistByDate[yIso]?.logged;
          const loggedToday = innerLogHistByDate[todayIso]?.logged;
          const showRecovery = loggedYesterday && !loggedToday && streakData.currentStreak === 0;
          return (
            <>
              {showRecovery && (
                <div style={{background:T.aiTagBg,border:`1px solid ${T.cardBorder}`,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:8,borderRadius:18}}>
                  <Lightning size={14} weight="fill" color={T.orange}/>
                  <span style={{color:T.inkDark,fontSize:11,fontFamily:T.fontDisplay,fontWeight:700}}>Keep going -- log today to protect your streak</span>
                </div>
              )}
              {(streakData.currentStreak>0 || showRecovery) && (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:13}}>
                  <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,padding:"12px 14px",textAlign:"center",borderRadius:18}}>
                    <p style={{margin:"0 0 2px",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>🔥 Current streak</p>
                    <p style={{margin:0,fontSize:32,fontFamily:T.fontDisplay,fontWeight:900,color:T.inkDark,lineHeight:1}}>
                      {streakData.currentStreak}<span style={{fontSize:14}}>{streakData.currentStreak===1?" day":" days"}</span>
                    </p>
                  </div>
                  <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,padding:"12px 14px",textAlign:"center",borderRadius:18}}>
                    <p style={{margin:"0 0 2px",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>🏆 Best streak</p>
                    <p style={{margin:0,fontSize:32,fontFamily:T.fontDisplay,fontWeight:900,color:T.inkDark,lineHeight:1}}>
                      {streakData.bestStreak}<span style={{fontSize:14}}>{streakData.bestStreak===1?" day":" days"}</span>
                    </p>
                    {earned.length > 0 && (
                      <div style={{display:"flex",gap:5,flexWrap:"wrap",justifyContent:"center",marginTop:8}}>
                        {earned.map(m => (
                          <div key={m} style={{
                            background:T.tileBg,border:`1px solid ${T.cardBorder}`,
                            padding:"3px 8px",display:"flex",alignItems:"center",gap:4,borderRadius:999,
                          }}>
                            <Trophy size={9} weight="fill" color={T.tileAccent}/>
                            <span style={{color:T.tileAccent,fontSize:9,fontFamily:T.fontDisplay,fontWeight:700}}>{m} days</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── Body Measurements ──────────────────────────────────────────── */}
        <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:14,overflow:"hidden"}}>
          <div style={{backgroundColor:T.tileBg,borderRadius:"18px 18px 0 0",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18}}>📏</span>
              <div>
                <div style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:700,color:T.tileAccent,lineHeight:1.1}}>Body measurements</div>
                <div style={{fontFamily:T.fontSans,fontSize:12,color:"rgba(255,255,255,0.5)"}}>Track composition over time</div>
              </div>
            </div>
            <button onClick={()=>setShowBodyMeas(v=>!v)} style={{
              background:T.orange,border:"none",borderRadius:T.radius,
              padding:"6px 14px",cursor:"pointer",color:"#fff",
              fontSize:11,fontFamily:T.fontDisplay,fontWeight:700,
            }}>+ Log</button>
          </div>
          {showBodyMeas&&(
            <div style={{padding:"0 16px 12px"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                {BODY_FIELDS.map(f=>(
                  <div key={f.key}>
                    <label style={{color:T.inkDark,fontSize:13,fontWeight:600,marginBottom:4,fontFamily:T.fontSans,display:"block"}}>{f.label} ({f.unit})</label>
                    <input type="number" step={f.step} value={bodyMeasForm[f.key]||""} onChange={e=>setBodyMeasForm(prev=>({...prev,[f.key]:e.target.value}))}
                      placeholder="0"
                      style={{width:"100%",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:10,color:T.inkDark,fontFamily:T.fontSans,fontSize:15,padding:"10px 14px",outline:"none",boxSizing:"border-box"}}/>
                  </div>
                ))}
              </div>
              <button onClick={()=>saveBodyMeasurement(bodyMeasForm)} disabled={bodyMeasLoading||Object.keys(bodyMeasForm).every(k=>!bodyMeasForm[k])} style={{
                width:"100%",background:T.orange,border:"none",borderRadius:T.radius,
                padding:"10px",cursor:"pointer",color:"#fff",fontSize:12,fontFamily:T.fontDisplay,fontWeight:700,
                opacity:bodyMeasLoading?0.6:1,
              }}>
                {bodyMeasLoading?"Saving...":"Save measurements"}
              </button>
            </div>
          )}
          {bodyMeasurements.length>0&&(
            <div style={{padding:"0 16px 14px"}}>
              {/* Latest entry summary */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                {BODY_FIELDS.filter(f=>bodyMeasurements[0][f.key]!=null).slice(0,6).map(f=>(
                  <div key={f.key} style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,padding:"8px 8px",textAlign:"center",borderRadius:T.radius}}>
                    <p style={{margin:0,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>{f.label}</p>
                    <p style={{margin:"2px 0 0",fontSize:15,fontFamily:T.fontDisplay,fontWeight:800,color:T.accent}}>{parseFloat(bodyMeasurements[0][f.key]).toFixed(1)}<span style={{fontSize:9,color:T.inkMuted}}>{f.unit}</span></p>
                  </div>
                ))}
              </div>
              {/* Sparklines for tracked fields */}
              {BODY_FIELDS.filter(f=>{
                const vals=bodyMeasurements.filter(m=>m[f.key]!=null);
                return vals.length>=2;
              }).map(f=>{
                const pts=[...bodyMeasurements].reverse().filter(m=>m[f.key]!=null).map(m=>({v:parseFloat(m[f.key]),date:m.date}));
                const first=pts[0].v, last=pts[pts.length-1].v, delta=+(last-first).toFixed(1);
                return(
                  <div key={f.key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${T.surfaceLight}`}}>
                    <div>
                      <p style={{margin:0,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted}}>{f.label}</p>
                      <p style={{margin:"1px 0 0",fontSize:13,fontFamily:T.fontDisplay,fontWeight:800,color:T.inkDark}}>{last.toFixed(1)}<span style={{fontSize:9,color:T.inkMuted}}>{f.unit}</span>
                        <span style={{fontSize:9,fontFamily:T.fontDisplay,color:delta<0?T.accent:delta>0?T.red:T.inkMuted,marginLeft:6}}>{delta>0?"+":""}{delta}</span>
                      </p>
                    </div>
                    <MeasurementSparkline data={pts} color={T.accent}/>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Body Composition Chart ──────────────────────────────────── */}
        {bodyMeasurements.length > 0 && (
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:14}}>
            <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",fontFamily:T.fontDisplay,margin:"0 0 10px"}}>Body composition</p>
            {(()=>{
              const latest = bodyMeasurements[0];
              const bf = parseFloat(latest.body_fat_pct);
              const hasBf = !isNaN(bf) && bf > 0;
              const last3 = bodyMeasurements.slice(0, 3);
              return (
                <div>
                  {/* Stacked bar */}
                  {hasBf && (
                    <div style={{marginBottom:12}}>
                      <div style={{display:"flex",height:24,borderRadius:12,overflow:"hidden",border:`1px solid ${T.cardBorder}`}}>
                        <div style={{width:`${bf}%`,background:T.red,transition:"width 0.4s"}}/>
                        <div style={{flex:1,background:T.surfaceLight}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                        <span style={{color:T.red,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>Body fat {bf.toFixed(1)}%</span>
                        <span style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>Lean mass {(100-bf).toFixed(1)}%</span>
                      </div>
                    </div>
                  )}
                  {/* Last 3 measurements table */}
                  {last3.length > 0 && (
                    <div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:2,padding:"6px 0",borderBottom:`1px solid ${T.cardBorder}`}}>
                        {["Date","Weight","BF%","Delta"].map(h=>(
                          <span key={h} style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,
                            letterSpacing:"0.5px"}}>{h}</span>
                        ))}
                      </div>
                      {last3.map((m,i)=>{
                        const prevBf = last3[i+1] ? parseFloat(last3[i+1].body_fat_pct) : null;
                        const curBf = parseFloat(m.body_fat_pct);
                        const delta = (prevBf && !isNaN(curBf) && !isNaN(prevBf)) ? +(curBf - prevBf).toFixed(1) : null;
                        return (
                          <div key={m.id||i} style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:2,
                            padding:"5px 0",borderBottom:`1px solid ${T.surfaceLight}`}}>
                            <span style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:600}}>
                              {fmtDate(m.date||m.logged_at?.slice(0,10)||"--")}
                            </span>
                            <span style={{color:T.inkDark,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>
                              {m.weight_kg ? `${parseFloat(m.weight_kg).toFixed(1)}kg` : "--"}
                            </span>
                            <span style={{color:T.inkDark,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>
                              {!isNaN(curBf)&&curBf>0 ? `${curBf.toFixed(1)}%` : "--"}
                            </span>
                            <span style={{color:delta!=null?(delta<0?T.accent:delta>0?T.red:T.inkMuted):T.inkMuted,
                              fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>
                              {delta!=null ? `${delta>0?"+":""}${delta}%` : "--"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Weight Log ──────────────────────────────────────────────── */}
        <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:14,overflow:"hidden"}}>
          <div style={{backgroundColor:T.tileBg,borderRadius:"18px 18px 0 0",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:36,height:36,borderRadius:18,backgroundColor:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:16}}>⚖️</span>
              </div>
              <div>
                <div style={{fontFamily:T.fontDisplay,fontSize:14,fontWeight:700,color:T.tileAccent,letterSpacing:"0.3px"}}>Weight journey</div>
                <div style={{fontFamily:T.fontSans,fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:2}}>
                  {weightLogs.length>0?`${weightLogs.length} entr${weightLogs.length===1?"y":"ies"} logged`:"No entries yet"}
                </div>
              </div>
            </div>
            {weightLogs.length>1&&(()=>{
              const _l=parseFloat(weightLogs[0].weight_kg);
              const _o=parseFloat(weightLogs[weightLogs.length-1].weight_kg);
              const _d=+(_l-_o).toFixed(1);
              return <span style={{color:_d<0?T.nutriGreen:_d>0?T.red:T.tileMuted,fontSize:14,fontWeight:700,fontFamily:T.fontDisplay}}>{_d>0?"+":""}{_d}kg</span>;
            })()}
          </div>
          <div style={{padding:"0 16px 14px"}}>
            {/* Entry row */}
            <div style={{display:"flex",gap:7,marginBottom:12}}>
              <input
                ref={weightInputRef}
                type="number"
                value={weightInput}
                onChange={e=>setWeightInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&logWeight(weightInput)}
                placeholder="e.g. 78.4"
                style={{flex:1,background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:8,
                  padding:"9px 12px",color:T.inkDark,fontSize:13,fontFamily:T.fontDisplay,
                  fontWeight:600,outline:"none",boxSizing:"border-box"}}
              />
              <span style={{display:"flex",alignItems:"center",color:T.inkMuted,fontSize:11,
                fontFamily:T.fontDisplay,fontWeight:600,flexShrink:0}}>kg</span>
              <button onClick={()=>logWeight(weightInput)} disabled={weightLogLoading||!weightInput} style={{
                background:T.orange,border:"none",borderRadius:T.radius,
                padding:"9px 16px",cursor:"pointer",color:"#fff",
                fontSize:11,fontFamily:T.fontDisplay,fontWeight:700,
                flexShrink:0,
                opacity:(!weightInput||weightLogLoading)?0.5:1,
              }}>
                {weightLogLoading?<Spinner size={10}/>:"Log"}
              </button>
            </div>
            {/* Sparkline + entries */}
            {weightLogs.length>0&&(()=>{
              const sorted = [...weightLogs].reverse(); // oldest->newest
              const vals   = sorted.map(w=>parseFloat(w.weight_kg));
              const mn     = Math.min(...vals)-1;
              const mx     = Math.max(...vals)+1;
              const W=280, H=60, PAD=10;
              const xS = i => PAD + (i/(vals.length-1||1))*(W-PAD*2);
              const yS = v => H - PAD - ((v-mn)/(mx-mn||1))*(H-PAD*2);
              const pathD = vals.map((v,i)=>`${i===0?"M":"L"}${xS(i).toFixed(1)},${yS(v).toFixed(1)}`).join(" ");
              const latest  = parseFloat(weightLogs[0].weight_kg);
              const oldest_ = parseFloat(weightLogs[weightLogs.length-1].weight_kg);
              const delta   = +(latest-oldest_).toFixed(1);
              const deltaColor = delta<0?T.accent:delta>0?T.red:T.inkMuted;
              return (
                <div>
                  <div style={{display:"flex",alignItems:"baseline",gap:9,marginBottom:8}}>
                    <span style={{color:T.accent,fontSize:28,fontWeight:800,fontFamily:T.fontDisplay,lineHeight:1}}>{latest}</span>
                    <span style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontDisplay,fontWeight:500}}>kg</span>
                  </div>
                  {vals.length>1&&(
                    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:"block",overflow:"visible",marginBottom:8}}>
                      <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke={T.inkFaint} strokeWidth={1}/>
                      <path d={pathD} fill="none" stroke={T.accent} strokeWidth={2.5} strokeLinejoin="round"/>
                      {vals.map((v,i)=>(
                        <circle key={i} cx={xS(i)} cy={yS(v)} r={3} fill={i===vals.length-1?T.accent:T.surfaceLight} stroke={T.inkFaint} strokeWidth={1.5}/>
                      ))}
                      <text x={xS(vals.length-1)} y={yS(vals[vals.length-1])-9} textAnchor="middle"
                        fill={T.accent} fontSize={11} fontFamily="'Courier New',monospace" fontWeight="bold">
                        {latest}
                      </text>
                      {vals.length>=3&&(()=>{
                        const n=vals.length;
                        const xMean=(n-1)/2;
                        const yMean=vals.reduce((s,v)=>s+v,0)/n;
                        const num=vals.reduce((s,v,i)=>s+(i-xMean)*(v-yMean),0);
                        const den=vals.reduce((s,v,i)=>s+(i-xMean)**2,0);
                        if(den===0)return null;
                        const slope=num/den;
                        const proj30=vals[n-1]+slope*30;
                        const _x1=xS(n-1);
                        const _y1=yS(vals[n-1]);
                        const _x2=_x1+50;
                        const _y2=yS(proj30);
                        return(
                          <>
                            <line x1={_x1.toFixed(1)} y1={_y1.toFixed(1)} x2={_x2.toFixed(1)} y2={_y2.toFixed(1)} stroke={T.inkFaint} strokeWidth={1.5} strokeDasharray="4,4" opacity={0.6}/>
                            <text x={_x2} y={Number(_y2)-7} fill={T.inkMuted} fontSize={10} fontFamily={T.fontSans} opacity={0.8}>~{proj30.toFixed(1)}kg</text>
                          </>
                        );
                      })()}
                    </svg>
                  )}
                  {/* Last 5 entries */}
                  <div style={{display:"flex",flexDirection:"column",gap:3}}>
                    {weightLogs.slice(0,5).map((w,i)=>(
                      <div key={w.id} style={{display:"flex",justifyContent:"space-between",
                        padding:"4px 0",borderBottom:i<4?`1px solid ${T.surfaceLight}`:"none"}}>
                        <span style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:500}}>
                          {new Date(w.logged_at).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}
                        </span>
                        <span style={{color:i===0?T.accent:T.inkMuted,fontSize:10,fontFamily:T.fontDisplay,fontWeight:700}}>
                          {parseFloat(w.weight_kg).toFixed(1)} kg
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {weightLogs.length===0&&(
              <p style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontDisplay,fontWeight:500,margin:0,fontStyle:"italic"}}>
                Log your weight daily to track progress over time.
              </p>
            )}
          </div>
        </div>

        {/* ── Health Connect 7-Day History (Part 1) ─────────────────── */}
        {hc.isNative && hc.isAvailable && (()=>{
          const hist = (() => { try { return JSON.parse(localStorage.getItem("np_health_history")||"[]"); } catch(_){ return []; }})();
          if(!hist || hist.length === 0) return null;
          const last7 = hist.slice(-7);
          const hasSteps = last7.some(d=>d.steps!=null);
          const hasCals  = last7.some(d=>d.activeCalories!=null);
          const hasSleep = last7.some(d=>d.sleepHours!=null);
          if(!hasSteps && !hasCals && !hasSleep) return null;
          const maxSteps = Math.max(...last7.map(d=>d.steps||0), 1);
          const maxCals  = Math.max(...last7.map(d=>d.activeCalories||0), 1);
          const maxSleep = Math.max(...last7.map(d=>d.sleepHours||0), 1);
          return (
            <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:14}}>
              <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",fontFamily:T.fontDisplay,margin:"0 0 10px"}}>Health Connect — 7-day</p>
              {hasSteps && (
                <div style={{marginBottom:12}}>
                  <p style={{margin:"0 0 4px",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>Steps</p>
                  <div style={{display:"flex",gap:3,alignItems:"flex-end",height:40}}>
                    {last7.map((d,i)=>{
                      const pct=(d.steps||0)/maxSteps;
                      return(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                        <div style={{width:"100%",background:T.accent,height:`${Math.max(pct*36,2)}px`,borderRadius:"2px 2px 0 0"}}/>
                        <span style={{fontSize:10,color:T.inkMuted,fontFamily:T.fontDisplay}}>{d.date?.slice(5)||""}</span>
                      </div>);
                    })}
                  </div>
                </div>
              )}
              {hasCals && (
                <div style={{marginBottom:12}}>
                  <p style={{margin:"0 0 4px",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>Active calories</p>
                  <div style={{display:"flex",gap:3,alignItems:"flex-end",height:40}}>
                    {last7.map((d,i)=>{
                      const pct=(d.activeCalories||0)/maxCals;
                      return(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                        <div style={{width:"100%",background:T.orange||"#f97316",height:`${Math.max(pct*36,2)}px`,borderRadius:"2px 2px 0 0"}}/>
                        <span style={{fontSize:10,color:T.inkMuted,fontFamily:T.fontDisplay}}>{d.date?.slice(5)||""}</span>
                      </div>);
                    })}
                  </div>
                </div>
              )}
              {hasSleep && (
                <div>
                  <p style={{margin:"0 0 4px",fontSize:10,fontFamily:T.fontDisplay,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>Sleep (hrs)</p>
                  <div style={{display:"flex",gap:3,alignItems:"flex-end",height:40}}>
                    {last7.map((d,i)=>{
                      const pct=(d.sleepHours||0)/maxSleep;
                      return(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                        <div style={{width:"100%",background:T.primary,height:`${Math.max(pct*36,2)}px`,borderRadius:"2px 2px 0 0"}}/>
                        <span style={{fontSize:10,color:T.inkMuted,fontFamily:T.fontDisplay}}>{d.date?.slice(5)||""}</span>
                      </div>);
                    })}
                  </div>
                </div>
              )}
              <p style={{margin:"6px 0 0",fontSize:9,color:T.inkMuted,fontFamily:T.fontDisplay,textAlign:"right"}}>Powered by Health Connect</p>
            </div>
          );
        })()}

        {/* V28 FIX 4: Achievement badges — show all, locked if not unlocked */}
        {ACHIEVEMENTS && ACHIEVEMENTS.length > 0 && (
          <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,overflow:"hidden",marginBottom:14}}>
            <div style={{backgroundColor:T.tileBg,borderRadius:"18px 18px 0 0",padding:"12px 16px",display:"flex",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
              <span style={{fontSize:18,marginRight:10}}>🏆</span>
              <div>
                <div style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:700,color:T.tileAccent,lineHeight:1.1}}>Achievements</div>
                <div style={{fontFamily:T.fontSans,fontSize:12,color:"rgba(255,255,255,0.5)"}}>Earned & locked milestones</div>
              </div>
            </div>
            <div style={{padding:16}}>
            {achievementToast && (
              <div style={{background:T.accent,padding:"8px 12px",marginBottom:10,borderRadius:T.radius,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{achievementToast.icon}</span>
                <div>
                  <p style={{margin:0,fontSize:11,fontFamily:T.fontDisplay,fontWeight:800,color:T.ink}}>{achievementToast.name}</p>
                  <p style={{margin:0,fontSize:9,fontFamily:T.fontDisplay,color:T.ink}}>{achievementToast.desc}</p>
                </div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
              {ACHIEVEMENTS.map(a => {
                const unlocked = (unlockedAchievements||[]).find(u => u.achievement_id === a.id);
                return (
                  <div key={a.id} style={{
                    background: unlocked ? T.card : T.surfaceLight,
                    border: unlocked ? `2px solid ${T.primary}` : `1px solid ${T.cardBorder}`,
                    borderRadius: T.radiusSm,
                    padding: "10px 6px",
                    textAlign: "center",
                  }}>
                    {unlocked ? (
                      <span style={{fontSize:22,display:"block",marginBottom:4}}>{a.icon}</span>
                    ) : (
                      <div style={{position:"relative",display:"inline-block",marginBottom:4}}>
                        <span style={{fontSize:22,display:"block",opacity:0.25,filter:"grayscale(100%)"}}>{a.icon}</span>
                        <div style={{position:"absolute",bottom:-2,right:-2,width:14,height:14,borderRadius:"50%",backgroundColor:T.cardBorder,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          <span style={{fontSize:10,lineHeight:1}}>🔒</span>
                        </div>
                      </div>
                    )}
                    <p style={{margin: 0, fontSize: 10, fontFamily: T.fontDisplay, fontWeight: 700,
                      color: unlocked ? T.inkDark : T.inkFaint, letterSpacing: "0.3px"}}>
                      {a.name}
                    </p>
                    <p style={{margin: "2px 0 0", fontSize: 9, fontFamily: T.fontDisplay,
                      color: T.inkMuted}}>
                      {a.desc}
                    </p>
                    {unlocked && (
                      <p style={{margin: "3px 0 0", fontSize: 9, fontFamily: T.fontDisplay, color: T.accent}}>
                        {new Date(unlocked.unlocked_at).toLocaleDateString("en-GB", {day:"numeric",month:"short"})}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        )}


      </div>
      <BottomNav screen={screen} setScreen={setScreen} hasTargets={true}/>
    </div></div>
  );
}
